import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, Bell, Menu, LogOut, Settings, HelpCircle, Moon, Sun, BookOpen, Compass, MessageCircle } from 'lucide-react';
import { requestTourOpen } from './PlatformTour';
import { useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { MobileNavDrawer } from './MobileNavDrawer';
import { CryptoFaqDialog } from './CryptoFaqDialog';
import { navigateInvestigationQuery } from '../utils/submitInvestigationQuery';
import {
    detectInputType,
    omnibarPickerChains,
    omnibarQueryIdentity,
    omnibarShownChain,
} from '../utils/WorkbenchSearch';
import { NotificationPanel } from './notifications/NotificationPanel';
import { useNotificationStore } from '../stores/useNotificationStore';
import { deploymentLabel } from '../utils/deploymentLabel';

interface UserInfo {
    username: string;
    full_name?: string | null;
    role: string;
}

export const Header: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [pickedChain, setPickedChain] = useState<string | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showHelpMenu, setShowHelpMenu] = useState(false);
    const [showCryptoFaq, setShowCryptoFaq] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const notifRef = useRef<HTMLDivElement>(null);
    const helpRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const unreadCount = useNotificationStore(s => s.unreadCount);
    const startPolling = useNotificationStore(s => s.startPolling);
    const stopPolling = useNotificationStore(s => s.stopPolling);
    const fetchList = useNotificationStore(s => s.fetchList);

    const isCompact = location.pathname === '/workbench';
    const detection = detectInputType(searchQuery);
    const pickerChains = omnibarPickerChains(detection);
    const shownChain = omnibarShownChain(pickedChain, null, detection.chain);
    const detectionQuery = detection.query || searchQuery;
    const queryIdentityRef = useRef<string | null>(null);

    useEffect(() => {
        const identity = omnibarQueryIdentity(detectionQuery);
        if (queryIdentityRef.current !== null && queryIdentityRef.current !== identity) {
            setPickedChain(null);
        }
        queryIdentityRef.current = identity;
    }, [detectionQuery]);

    const [isDarkTheme, setIsDarkTheme] = useState(() => {
        const storedTheme = localStorage.getItem('theme');
        if (storedTheme) return storedTheme === 'dark';
        return true;
    });

    const toggleTheme = () => {
        setIsDarkTheme(prev => {
            const next = !prev;
            if (next) {
                document.documentElement.classList.add('dark');
                localStorage.setItem('theme', 'dark');
            } else {
                document.documentElement.classList.remove('dark');
                localStorage.setItem('theme', 'light');
            }
            return next;
        });
    };

    useEffect(() => {
        if (isDarkTheme) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDarkTheme]);

    // Close mobile drawer / menus on route change
    useEffect(() => {
        setMobileNavOpen(false);
        setShowNotifications(false);
        setShowUserMenu(false);
        setShowHelpMenu(false);
        setShowCryptoFaq(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        startPolling();
        return () => stopPolling();
    }, [startPolling, stopPolling]);

    const userInfo = useMemo<UserInfo | null>(() => {
        try {
            const raw = localStorage.getItem('user');
            if (raw) return JSON.parse(raw);
        } catch { /* ignore */ }
        return null;
    }, []);

    const initials = useMemo(() => {
        if (userInfo?.full_name) {
            return userInfo.full_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
        }
        if (userInfo?.username) return userInfo.username.slice(0, 2).toUpperCase();
        return '?';
    }, [userInfo]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowUserMenu(false);
            }
            if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
                setShowNotifications(false);
            }
            if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
                setShowHelpMenu(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setShowUserMenu(false);
                setShowNotifications(false);
                setShowHelpMenu(false);
            }
        };
        if (showUserMenu || showNotifications || showHelpMenu) {
            document.addEventListener('mousedown', handleClick);
            document.addEventListener('keydown', handleKey);
        }
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [showUserMenu, showNotifications, showHelpMenu]);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchQuery.trim() || isSearching) return;

        setIsSearching(true);
        try {
            const ok = navigateInvestigationQuery(searchQuery, navigate, { pickedChain });
            if (ok) {
                setSearchQuery('');
                setPickedChain(null);
            }
        } finally {
            setIsSearching(false);
        }
    };

    const handleLogout = () => {
        // Credentials are cleared by the login screen, not here. This navigation
        // is interceptable — the Workbench blocks it to offer "save before
        // leaving" — and that save needs a live token to list cases, create a
        // case, and persist the investigation.
        navigate('/login?logout=1');
    };

    const envLabel = deploymentLabel(
        typeof window !== 'undefined' ? window.location.hostname : '',
        import.meta.env.VITE_APP_ENV,
    );

    return (
        <>
            <header className={clsx(
                // DashboardSectionNav is sticky at z-20. Keep the global header
                // in a higher stacking context so its account menu cannot be
                // painted underneath page chrome. shrink-0 also preserves the
                // declared height inside Layout's h-screen flex column.
                "bg-card px-4 flex items-center justify-between z-[100] relative shrink-0 overflow-visible transition-colors",
                isCompact ? "h-12 border-b-2 border-b-primary" : "h-16 border-b border-border"
            )}>
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-accent rounded-md lg:hidden"
                        aria-label="Open navigation menu"
                        onClick={() => setMobileNavOpen(true)}
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className={clsx(
                            // Theme-matched assets: light UI → light mark (+ light chip),
                            // dark UI → dark mark (+ ink chip). *-light has dark ink strokes;
                            // *-dark has white strokes.
                            "flex items-center justify-center rounded-lg bg-white dark:bg-brand-ink ring-1 ring-primary/30",
                            isCompact ? "w-7 h-7" : "w-9 h-9"
                        )}>
                            <img
                                src="/brand/cryptoflow-icon-light.svg"
                                alt="CryptoFlow"
                                className={clsx('block dark:hidden', isCompact ? 'h-4 w-4' : 'h-5 w-5')}
                            />
                            <img
                                src="/brand/cryptoflow-icon-dark.svg"
                                alt="CryptoFlow"
                                className={clsx('hidden dark:block', isCompact ? 'h-4 w-4' : 'h-5 w-5')}
                            />
                        </div>
                        <span className={clsx("text-foreground font-semibold", isCompact ? "text-xs" : "text-sm")}>CryptoFlow</span>
                        {envLabel && (
                            <span
                                data-testid="deployment-label"
                                className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground"
                            >
                                {envLabel}
                            </span>
                        )}
                    </div>
                </div>

                {!isCompact && (
                    <form
                        onSubmit={handleSearch}
                        className="flex-1 max-w-2xl mx-4 flex items-center gap-2 min-w-0"
                        data-tour="global-omnibar"
                        data-testid="global-omnibar"
                        aria-label="Investigate address or transaction"
                    >
                        <div className="flex flex-1 min-w-0 items-center gap-1.5 rounded-lg border border-input bg-card pl-3 pr-1 py-1 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-colors">
                            <Search className="w-4 h-4 text-muted-foreground pointer-events-none shrink-0" aria-hidden />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Address, transaction hash, or UPI…"
                                className="min-w-0 flex-1 bg-transparent border-0 py-1 text-sm outline-none shadow-none focus:ring-0"
                                disabled={isSearching}
                                aria-label="Address, transaction hash, or UPI"
                                data-testid="global-omnibar-input"
                                autoComplete="one-time-code"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                            />
                            {searchQuery.trim() && detection.label && (
                                <div
                                    className="flex shrink-0 items-center gap-1 max-w-[50%] sm:max-w-[54%]"
                                    data-testid="global-omnibar-chain-badge"
                                >
                                    <span
                                        className={`truncate px-1.5 py-0.5 text-[10px] uppercase font-medium rounded tracking-wide border
                                        ${detection.type === 'unknown'
                                            ? 'bg-muted text-muted-foreground border-border'
                                            : detection.isTx
                                                ? 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/25'
                                                : 'bg-primary/10 text-primary border-primary/25'
                                        }`}
                                        title={[
                                            detection.label,
                                            pickerChains ? pickerChains.join('/') : null,
                                        ].filter(Boolean).join(' · ')}
                                    >
                                        {detection.label}
                                    </span>
                                    {pickerChains && (
                                        <select
                                            className="h-6 max-w-[5.5rem] shrink-0 rounded border border-primary/25 bg-primary/10 px-1 text-[10px] uppercase font-medium tracking-wide text-primary outline-none focus:ring-2 focus:ring-primary/20"
                                            value={shownChain || pickerChains[0]}
                                            onChange={(e) => setPickedChain(e.target.value)}
                                            aria-label="Select chain for this EVM address"
                                            data-testid="global-omnibar-chain-select"
                                            title="Same 0x address can be a different entity on each EVM chain. Pick the chain from the test case or investigation."
                                        >
                                            {pickerChains.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            )}
                        </div>
                        <button
                            type="submit"
                            disabled={isSearching || !searchQuery.trim()}
                            className="shrink-0 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                            data-testid="global-omnibar-investigate"
                            data-tour="global-omnibar-investigate"
                        >
                            {isSearching ? 'Opening…' : 'Investigate'}
                        </button>
                    </form>
                )}

                <div className="flex items-center gap-2">
                    <button
                        onClick={toggleTheme}
                        role="switch"
                        aria-checked={isDarkTheme}
                        aria-label={isDarkTheme ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        title={isDarkTheme ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        className={clsx(
                            "relative inline-flex items-center rounded-full border transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/40",
                            isCompact ? "h-6 w-12" : "h-7 w-14",
                            isDarkTheme
                                ? "bg-secondary border-border hover:bg-muted"
                                : "bg-secondary border-border hover:bg-muted"
                        )}
                    >
                        {isDarkTheme ? (
                            <Sun className={clsx("absolute left-1.5 text-muted-foreground", isCompact ? "w-3.5 h-3.5" : "w-4 h-4")} />
                        ) : (
                            <Moon className={clsx("absolute right-1.5 text-muted-foreground", isCompact ? "w-3.5 h-3.5" : "w-4 h-4")} />
                        )}
                        <span
                            className={clsx(
                                "absolute left-0.5 flex items-center justify-center rounded-full border border-border transition-transform duration-200",
                                isCompact ? "h-5 w-5" : "h-6 w-6",
                                isDarkTheme
                                    ? clsx("bg-brand-ink ring-1 ring-border", isCompact ? "translate-x-6" : "translate-x-7")
                                    : clsx("bg-brand-cream ring-1 ring-primary/30", "translate-x-0")
                            )}
                        >
                            {isDarkTheme ? (
                                <Moon className={clsx("text-foreground", isCompact ? "w-3 h-3" : "w-3.5 h-3.5")} />
                            ) : (
                                <Sun className={clsx("text-brand-ink", isCompact ? "w-3 h-3" : "w-3.5 h-3.5")} />
                            )}
                        </span>
                    </button>
                    <div className="relative" ref={helpRef}>
                        <button
                            type="button"
                            onClick={() => {
                                const next = !showHelpMenu;
                                setShowHelpMenu(next);
                                setShowNotifications(false);
                                setShowUserMenu(false);
                            }}
                            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-accent rounded-full"
                            title="Help"
                            aria-label="Help menu"
                            data-tour="header-help"
                            aria-expanded={showHelpMenu}
                            aria-haspopup="menu"
                            aria-controls={showHelpMenu ? 'help-menu' : undefined}
                        >
                            <HelpCircle className={clsx("text-muted-foreground hover:text-primary transition-colors", isCompact ? "w-4 h-4" : "w-5 h-5")} />
                        </button>
                        {showHelpMenu && (
                            <div
                                id="help-menu"
                                role="menu"
                                aria-label="Help options"
                                className="absolute right-0 top-[calc(100%+0.5rem)] z-[110] w-56 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-overlay"
                            >
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                        setShowHelpMenu(false);
                                        requestTourOpen();
                                    }}
                                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-foreground transition-colors hover:bg-muted"
                                >
                                    <Compass className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                    <span>
                                        <span className="block text-sm font-medium">Platform Tour</span>
                                        <span className="block text-xs text-muted-foreground">Learn where key tools are</span>
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                        setShowHelpMenu(false);
                                        navigate('/documentation');
                                    }}
                                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-foreground transition-colors hover:bg-muted"
                                >
                                    <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                    <span>
                                        <span className="block text-sm font-medium">Documentation</span>
                                        <span className="block text-xs text-muted-foreground">Glossary, what’s new, investigation guide</span>
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                        setShowHelpMenu(false);
                                        setShowCryptoFaq(true);
                                    }}
                                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left text-foreground transition-colors hover:bg-muted"
                                >
                                    <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                                    <span>
                                        <span className="block text-sm font-medium">Crypto FAQ</span>
                                        <span className="block text-xs text-muted-foreground">Quick search of investigation terms</span>
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="relative" ref={notifRef}>
                        <button
                            type="button"
                            onClick={() => {
                                const next = !showNotifications;
                                setShowNotifications(next);
                                setShowUserMenu(false);
                                if (next) void fetchList();
                            }}
                            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-accent rounded-full relative cursor-pointer"
                            title="Notifications"
                            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                            aria-expanded={showNotifications}
                            aria-haspopup="menu"
                            aria-controls={showNotifications ? 'notification-panel' : undefined}
                            data-tour="header-notifications"
                            data-testid="notification-bell"
                        >
                            <Bell className={clsx("text-muted-foreground", isCompact ? "w-4 h-4" : "w-5 h-5")} />
                            {unreadCount > 0 && (
                                <span
                                    className="absolute top-1.5 right-1.5 min-w-[1rem] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold leading-4 text-center"
                                >
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>
                        {showNotifications && <NotificationPanel />}
                    </div>

                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={() => { setShowNotifications(false); setShowUserMenu(!showUserMenu); }}
                            className={clsx(
                                "bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 text-xs",
                                isCompact ? "w-7 h-7" : "w-8 h-8"
                            )}
                            title={userInfo?.full_name || userInfo?.username || 'Account'}
                            aria-label="Account menu"
                            aria-expanded={showUserMenu}
                            aria-haspopup="menu"
                            aria-controls={showUserMenu ? 'account-menu' : undefined}
                        >
                            {initials}
                        </button>

                        {showUserMenu && (
                            <div
                                id="account-menu"
                                role="menu"
                                aria-label="Account options"
                                data-testid="account-menu"
                                className="absolute right-0 top-[calc(100%+0.5rem)] w-56 bg-card border border-border rounded-lg shadow-overlay py-1 z-[110]"
                            >
                                <div className="px-4 py-2.5 border-b border-border">
                                    <p className="text-sm font-medium text-foreground truncate">
                                        {userInfo?.full_name || userInfo?.username || 'User'}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {userInfo?.username}{userInfo?.role ? ` \u00b7 ${userInfo.role}` : ''}
                                    </p>
                                </div>
                                <button
                                    onClick={() => { setShowUserMenu(false); navigate('/documentation'); }}
                                    className="w-full px-4 py-2 text-sm text-left hover:bg-muted flex items-center gap-2 text-foreground transition-colors"
                                    role="menuitem"
                                >
                                    <BookOpen className="w-4 h-4" />
                                    <span>Documentation</span>
                                </button>
                                <button
                                    onClick={() => { setShowUserMenu(false); navigate('/feedback'); }}
                                    className="w-full px-4 py-2 text-sm text-left hover:bg-muted flex items-center gap-2 text-foreground transition-colors"
                                    role="menuitem"
                                >
                                    <MessageCircle className="w-4 h-4" />
                                    <span>Feedback</span>
                                </button>
                                <button
                                    onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                                    className="w-full px-4 py-2 text-sm text-left hover:bg-muted flex items-center gap-2 text-foreground transition-colors"
                                    role="menuitem"
                                >
                                    <Settings className="w-4 h-4" />
                                    <span>Settings</span>
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="w-full px-4 py-2 text-sm text-left hover:bg-destructive/10 flex items-center gap-2 text-red-600 dark:text-red-400 font-medium"
                                    role="menuitem"
                                >
                                    <LogOut className="w-4 h-4" />
                                    <span>Log Out</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>
            <MobileNavDrawer isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
            <CryptoFaqDialog open={showCryptoFaq} onClose={() => setShowCryptoFaq(false)} />
        </>
    );
};
